import { Component, ElementRef, HostListener, Inject, ViewChild } from "@angular/core"
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog"
import { HDImage } from "@api/communication/models/hotel-directory"
import { MediaLibraryCleanupService } from "@shared/services/media-library-cleanup.service"
import { UtilityApiService } from "@api/utility/utility-api.service"
import { IconImageService } from "@shared/services/icon-image.service"
import { StateService } from "@shared/services/state.service"
import { ToastServiceV2 } from "@shared/services/toast-v2/toast-v2.service"
import { HotelDirectoryService } from "app/hotel-directory/hotel-directory.service"
import { Environment } from "environments/environment"
import { v4 as uuidv4 } from "uuid"

@Component({
  selector: "image-adjust-dialog",
  standalone: false,
  templateUrl: "./image-adjust-dialog.component.html",
  styleUrl: "./image-adjust-dialog.component.scss",
})
export class ImageAdjustDialogComponent {
  image: HDImage
  imageFit: "Original" | "Fill" | "Crop" = "Original"
  shownAreaHeight: number = 0
  shownAreaWidth: number = 0
  shownAreaAspectRatio: number = 0
  imageAspectRatio: number = 0
  originalImgHeight: number = 0
  originalImgWidth: number = 0
  imgHeight: number = 0
  imgWidth: number = 0
  private outputWidth: number = 0
  private outputHeight: number = 0
  private cropZoomFactor: number = 1
  private baseImgWidth: number = 0
  private baseImgHeight: number = 0
  scaledPaddingLeft: number = 0
  scaledPaddingRight: number = 0
  scaledPaddingTop: number = 0
  scaledPaddingBottom: number = 0
  private paddingLeft: number = 0
  private paddingRight: number = 0
  private paddingTop: number = 0
  private paddingBottom: number = 0
  hotelId: string = ""
  isLoading: boolean = false
  uploading: boolean = false
  imageSrc: string = ""
  isEmailBuilder: boolean = false
  emailBuilderId: string = ""
  emailBuilderName: string = ""

  dialogboxWidth: number = 848
  dialogboxHeight: number = 444
  dialogboxAspectRatio: number = this.dialogboxWidth / this.dialogboxHeight

  // Crop box in image-local pixels (what the user wants to keep)
  cropBox = { x: 0, y: 0, w: 0, h: 0 }

  fillDragX: number = 0
  fillDragY: number = 0
  isFillDragging: boolean = false
  cropDragX: number = 0
  cropDragY: number = 0
  isCropPanning: boolean = false
  private cropPanStart: { x: number; y: number } | null = null

  private activeCropHandle: "top" | "bottom" | "left" | "right" | "topleft" | "topright" | "bottomleft" | "bottomright" | "move" | null = null
  private cropDragStart: { x: number; y: number } | null = null
  private fillDragStart: { x: number; y: number } | null = null
  private readonly minCropPx = 20

  @ViewChild("imageElement", { static: true }) imageElement: ElementRef
  @ViewChild("shownArea") shownArea: ElementRef
  @ViewChild("blackBox", { static: true }) blackBox: ElementRef

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private stateService: StateService,
    public dialogRef: MatDialogRef<ImageAdjustDialogComponent>,
    public iconService: IconImageService,
    private utilityApiService: UtilityApiService,
    private hdService: HotelDirectoryService,
    private toasterService: ToastServiceV2,
    private mediaLibraryCleanupService: MediaLibraryCleanupService,
  ) {
    this.isLoading = true
    this.hotelId = this.stateService.selectedHotel.Id
    this.image = data.image
    console.log("Received image for cropping:", this.data)
    this.isEmailBuilder = data?.emailBuilder
    if (this.isEmailBuilder) {
      this.emailBuilderId = data?.emailBuilderId
      this.emailBuilderName = data?.emailBuilderName
    }
    if (this.image?.imageCropping && typeof this.image?.imageCropping === "string") this.image.imageCropping = JSON.parse(this.image?.imageCropping)
    this.shownAreaHeight = data.imageHeight
    this.shownAreaWidth = data.imageWidth
    this.outputWidth = data.imageWidth
    this.outputHeight = data.imageHeight
    this.paddingLeft = data.paddingLeft ?? 0
    this.paddingRight = data.paddingRight ?? 0
    this.paddingTop = data.paddingTop ?? 0
    this.paddingBottom = data.paddingBottom ?? 0
    this.shownAreaAspectRatio = this.shownAreaWidth / this.shownAreaHeight
    this.loadImage()
  }

  loadImage() {
    const img = new Image()
    img.src = this.image.imageCropping?.oldImageLink ?? this.image.imageLink
    this.imageSrc = img.src

    img.onload = () => {
      this.imageAspectRatio = img.width / img.height

      // Scale original image to fit the dialog box
      const scaleFactor = img.height > this.dialogboxHeight ? this.dialogboxHeight / img.height : 1
      this.originalImgHeight = img.height * scaleFactor
      this.originalImgWidth = img.width * scaleFactor

      // Scale shownArea to fit nicely within the dialog
      const resizeFactor = this.shownAreaAspectRatio < this.dialogboxAspectRatio || this.shownAreaAspectRatio < 1 ? (this.dialogboxHeight * 0.85) / this.shownAreaHeight : (this.dialogboxWidth * 0.85) / this.shownAreaWidth
      this.shownAreaHeight = this.shownAreaHeight * resizeFactor
      this.shownAreaWidth = this.shownAreaWidth * resizeFactor
      this.scaledPaddingLeft = this.paddingLeft * resizeFactor
      this.scaledPaddingRight = this.paddingRight * resizeFactor
      this.scaledPaddingTop = this.paddingTop * resizeFactor
      this.scaledPaddingBottom = this.paddingBottom * resizeFactor

      this.setOriginalImageSize()
      if (this.image?.imageCropping?.imageFit) {
        this.onImageFitChange(this.image.imageCropping.imageFit)
        if (this.image.imageCropping.imageFit === "Fill") {
          const scaleX = img.width / this.imgWidth
          const scaleY = img.height / this.imgHeight
          const maxDragX = Math.max(0, (this.imgWidth - this.shownAreaWidth) / 2)
          const maxDragY = Math.max(0, (this.imgHeight - this.shownAreaHeight) / 2)
          const rawDragX = (this.imgWidth - this.shownAreaWidth) / 2 - this.image.imageCropping.cropLeft / scaleX
          const rawDragY = (this.imgHeight - this.shownAreaHeight) / 2 - this.image.imageCropping.cropTop / scaleY
          this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, rawDragX))
          this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, rawDragY))
        } else if (this.image.imageCropping.imageFit === "Crop") {
          const scaleX = img.width / this.imgWidth
          const scaleY = img.height / this.imgHeight
          const cropLeft = this.image.imageCropping.cropLeft / scaleX
          const cropTop = this.image.imageCropping.cropTop / scaleY
          const cropRight = this.image.imageCropping.cropRight / scaleX
          const cropBottom = this.image.imageCropping.cropBottom / scaleY
          const w = Math.max(this.minCropPx, this.imgWidth - cropLeft - cropRight)
          const h = Math.max(this.minCropPx, this.imgHeight - cropTop - cropBottom)
          const b = this.cropBoxEffectiveBounds
          const x = Math.max(b.minX, Math.min(b.maxX - w, cropLeft))
          const y = Math.max(b.minY, Math.min(b.maxY - h, cropTop))
          this.cropBox = { x, y, w, h }
        }
      }
      this.isLoading = false
    }
  }

  onImageFitChange(fit: string) {
    if (fit !== "Fill") {
      this.fillDragX = 0
      this.fillDragY = 0
    }
    if (fit === "Original") {
      this.imageFit = "Original"
      this.setOriginalImageSize()
    } else if (fit === "Fill") {
      this.imageFit = "Fill"
      this.setFillImageSize()
    } else if (fit === "Crop") {
      this.imageFit = "Crop"
      this.setOriginalImageSize()
      this.cropZoomFactor = 1
      this.cropDragX = 0
      this.cropDragY = 0
      this.baseImgWidth = this.imgWidth
      this.baseImgHeight = this.imgHeight
      const b = this.cropBoxEffectiveBounds
      this.cropBox = { x: b.minX, y: b.minY, w: b.maxX - b.minX, h: b.maxY - b.minY }
    }
  }

  get innerWidth(): number {
    return this.shownAreaWidth
  }

  get innerHeight(): number {
    return this.shownAreaHeight
  }

  get hasPadding(): boolean {
    return this.scaledPaddingLeft > 0 || this.scaledPaddingRight > 0 || this.scaledPaddingTop > 0 || this.scaledPaddingBottom > 0
  }

  get paddingClipPath(): string {
    const totalW = this.shownAreaWidth + this.scaledPaddingLeft + this.scaledPaddingRight
    const totalH = this.shownAreaHeight + this.scaledPaddingTop + this.scaledPaddingBottom
    const lp = ((this.scaledPaddingLeft / totalW) * 100).toFixed(3)
    const rp = (((totalW - this.scaledPaddingRight) / totalW) * 100).toFixed(3)
    const tp = ((this.scaledPaddingTop / totalH) * 100).toFixed(3)
    const bp = (((totalH - this.scaledPaddingBottom) / totalH) * 100).toFixed(3)
    return `polygon(0% 0%, 0% 100%, ${lp}% 100%, ${lp}% ${tp}%, ${rp}% ${tp}%, ${rp}% ${bp}%, ${lp}% ${bp}%, ${lp}% 100%, 100% 100%, 100% 0%)`
  }

  get imgTransform(): string {
    if (this.imageFit === "Original") {
      const innerCenterX = (this.scaledPaddingLeft - this.scaledPaddingRight) / 2
      const innerTopY = (this.scaledPaddingTop - this.scaledPaddingBottom) / 2 - this.innerHeight / 2
      return `translate(${-this.imgWidth / 2 + innerCenterX}px, ${innerTopY}px)`
    }

    if (this.imageFit === "Crop") {
      return `translate(${-this.imgWidth / 2 + this.cropDragX}px, ${-this.shownAreaHeight / 2 + this.cropDragY}px)`
    }

    return `translate(${-this.imgWidth / 2 + this.fillDragX}px, ${-this.imgHeight / 2 + this.fillDragY}px)`
  }

  get overlayClipPath(): string {
    const bW = this.dialogboxWidth
    const bH = this.dialogboxHeight
    const totalW = this.hasPadding ? this.shownAreaWidth + this.scaledPaddingLeft + this.scaledPaddingRight : this.shownAreaWidth
    const totalH = this.hasPadding ? this.shownAreaHeight + this.scaledPaddingTop + this.scaledPaddingBottom : this.shownAreaHeight
    const lp = (((bW - totalW) / 2 / bW) * 100).toFixed(3)
    const rp = (((bW + totalW) / 2 / bW) * 100).toFixed(3)
    const tp = (((bH - totalH) / 2 / bH) * 100).toFixed(3)
    const bp = (((bH + totalH) / 2 / bH) * 100).toFixed(3)
    return `polygon(0% 0%, 0% 100%, ${lp}% 100%, ${lp}% ${tp}%, ${rp}% ${tp}%, ${rp}% ${bp}%, ${lp}% ${bp}%, ${lp}% 100%, 100% 100%, 100% 0%)`
  }

  onImageMouseDown(event: MouseEvent) {
    if (this.imageFit !== "Fill") return
    event.preventDefault()
    this.isFillDragging = true
    this.fillDragStart = { x: event.clientX, y: event.clientY }
  }

  setOriginalImageSize() {
    const fitWidth = this.innerWidth
    const fitHeight = this.innerHeight
    const fitAspectRatio = fitWidth / fitHeight
    if (this.imageAspectRatio > fitAspectRatio) {
      this.imgWidth = fitWidth
      this.imgHeight = fitWidth / this.imageAspectRatio
    } else {
      this.imgHeight = fitHeight
      this.imgWidth = fitHeight * this.imageAspectRatio
    }
  }

  // Cover the shownArea (fills completely, may overflow one axis)
  setFillImageSize() {
    if (this.imageAspectRatio > this.shownAreaAspectRatio) {
      this.imgHeight = this.shownAreaHeight
      this.imgWidth = this.shownAreaHeight * this.imageAspectRatio
    } else {
      this.imgWidth = this.shownAreaWidth
      this.imgHeight = this.shownAreaWidth / this.imageAspectRatio
    }
    const maxDragX = Math.max(0, (this.imgWidth - this.shownAreaWidth) / 2)
    const maxDragY = Math.max(0, (this.imgHeight - this.shownAreaHeight) / 2)
    this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, this.fillDragX))
    this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, this.fillDragY))
  }

  get imageOffsetX(): number {
    return (this.dialogboxWidth - this.imgWidth) / 2 + (this.imageFit === "Crop" ? this.cropDragX : 0)
  }

  get imageOffsetY(): number {
    if (this.imageFit === "Crop") {
      return (this.dialogboxHeight - this.shownAreaHeight) / 2 + this.cropDragY
    }
    return (this.dialogboxHeight - this.imgHeight) / 2
  }

  private get cropBoxEffectiveBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    const shownMinX = (this.imgWidth - this.shownAreaWidth) / 2 - this.cropDragX
    const shownMinY = (this.imgHeight - this.shownAreaHeight) / 2 - this.cropDragY
    return {
      minX: Math.max(0, shownMinX),
      maxX: Math.min(this.imgWidth, shownMinX + this.shownAreaWidth),
      minY: Math.max(0, shownMinY),
      maxY: Math.min(this.imgHeight, shownMinY + this.shownAreaHeight),
    }
  }

  get screenCropLeft(): number {
    return this.imageOffsetX + this.cropBox.x
  }

  get screenCropTop(): number {
    return this.imageOffsetY + this.cropBox.y
  }

  onCropBoxDown(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.activeCropHandle = "move"
    this.cropDragStart = { x: event.clientX, y: event.clientY }
  }

  onBlackBoxMouseDown(event: MouseEvent) {
    if (this.imageFit !== "Crop") return
    event.preventDefault()
    this.isCropPanning = true
    this.cropPanStart = { x: event.clientX, y: event.clientY }
  }

  onCropHandleDown(event: MouseEvent, handle: "top" | "bottom" | "left" | "right" | "topleft" | "topright" | "bottomleft" | "bottomright") {
    event.preventDefault()
    event.stopPropagation()
    this.activeCropHandle = handle
    this.cropDragStart = { x: event.clientX, y: event.clientY }
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent) {
    if (this.isFillDragging && this.fillDragStart) {
      const dx = event.clientX - this.fillDragStart.x
      const dy = event.clientY - this.fillDragStart.y
      this.fillDragStart = { x: event.clientX, y: event.clientY }
      const maxDragX = Math.max(0, (this.imgWidth - this.shownAreaWidth) / 2)
      const maxDragY = Math.max(0, (this.imgHeight - this.shownAreaHeight) / 2)
      this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, this.fillDragX + dx))
      this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, this.fillDragY + dy))
      return
    }

    if (this.isCropPanning && this.cropPanStart) {
      const dx = event.clientX - this.cropPanStart.x
      const dy = event.clientY - this.cropPanStart.y
      this.cropPanStart = { x: event.clientX, y: event.clientY }
      const maxDragX = Math.max(0, (this.imgWidth - this.shownAreaWidth) / 2)
      const maxDragY = Math.max(0, (this.imgHeight - this.shownAreaHeight) / 2)
      const newDragX = Math.max(-maxDragX, Math.min(maxDragX, this.cropDragX + dx))
      const newDragY = Math.max(-maxDragY, Math.min(maxDragY, this.cropDragY + dy))
      const actualDx = newDragX - this.cropDragX
      const actualDy = newDragY - this.cropDragY
      this.cropDragX = newDragX
      this.cropDragY = newDragY
      // Keep crop box screen-fixed: image moved by actualDx, so crop box image-coord moves by -actualDx
      const b = this.cropBoxEffectiveBounds
      this.cropBox.x = Math.max(b.minX, Math.min(b.maxX - this.cropBox.w, this.cropBox.x - actualDx))
      this.cropBox.y = Math.max(b.minY, Math.min(b.maxY - this.cropBox.h, this.cropBox.y - actualDy))
      return
    }

    if (!this.activeCropHandle || !this.cropDragStart) return

    const dx = event.clientX - this.cropDragStart.x
    const dy = event.clientY - this.cropDragStart.y
    this.cropDragStart = { x: event.clientX, y: event.clientY }
    const b = this.cropBoxEffectiveBounds

    switch (this.activeCropHandle) {
      case "move": {
        this.cropBox.x = Math.max(b.minX, Math.min(b.maxX - this.cropBox.w, this.cropBox.x + dx))
        this.cropBox.y = Math.max(b.minY, Math.min(b.maxY - this.cropBox.h, this.cropBox.y + dy))
        break
      }
      case "top": {
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= b.minY && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "bottom": {
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= b.maxY) {
          this.cropBox.h = newH
        }
        break
      }
      case "left": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= b.minX && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        break
      }
      case "right": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= b.maxX) {
          this.cropBox.w = newW
        }
        break
      }
      case "topleft": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= b.minX && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= b.minY && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "topright": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= b.maxX) {
          this.cropBox.w = newW
        }
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= b.minY && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "bottomleft": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= b.minX && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= b.maxY) {
          this.cropBox.h = newH
        }
        break
      }
      case "bottomright": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= b.maxX) {
          this.cropBox.w = newW
        }
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= b.maxY) {
          this.cropBox.h = newH
        }
        break
      }
    }
  }

  @HostListener("document:mouseup")
  onMouseUp() {
    this.activeCropHandle = null
    this.cropDragStart = null
    this.isFillDragging = false
    this.fillDragStart = null
    this.isCropPanning = false
    this.cropPanStart = null
  }

  onWheel(event: WheelEvent) {
    if (this.imageFit !== "Crop") return
    event.preventDefault()

    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1
    const newZoom = Math.max(1, Math.min(10, this.cropZoomFactor * factor))
    const actualFactor = newZoom / this.cropZoomFactor
    this.cropZoomFactor = newZoom

    const oldImgWidth = this.imgWidth
    const oldImgHeight = this.imgHeight
    this.imgWidth = this.baseImgWidth * this.cropZoomFactor
    this.imgHeight = this.baseImgHeight * this.cropZoomFactor

    // Shift crop box so its screen position stays fixed as the image scales
    this.cropBox.x += (oldImgWidth * (actualFactor - 1)) / 2
    this.cropBox.y += (oldImgHeight * (actualFactor - 1)) / 2

    // Clamp pan offset within new image bounds
    const maxDragX = Math.max(0, (this.imgWidth - this.shownAreaWidth) / 2)
    const maxDragY = Math.max(0, (this.imgHeight - this.shownAreaHeight) / 2)
    this.cropDragX = Math.max(-maxDragX, Math.min(maxDragX, this.cropDragX))
    this.cropDragY = Math.max(-maxDragY, Math.min(maxDragY, this.cropDragY))

    // Clamp crop box within shown area bounds
    const b = this.cropBoxEffectiveBounds
    this.cropBox.w = Math.min(this.cropBox.w, b.maxX - b.minX)
    this.cropBox.h = Math.min(this.cropBox.h, b.maxY - b.minY)
    this.cropBox.x = Math.max(b.minX, Math.min(b.maxX - this.cropBox.w, this.cropBox.x))
    this.cropBox.y = Math.max(b.minY, Math.min(b.maxY - this.cropBox.h, this.cropBox.y))
  }

  close() {
    this.dialogRef.close(false)
  }

  save() {
    if (this.imageFit === "Original") {
      const originalLink = this.image.imageCropping?.oldImageLink ?? this.image.imageLink
      this.image.imageLink = originalLink
      this.image.imageCropping = null
      this.dialogRef.close(this.image)
      return
    }
    this.uploadCroppedImageToBlob()
  }

  private computeCropPx(realImgWidth: number, realImgHeight: number): { left: number; top: number; right: number; bottom: number } {
    if (this.imageFit === "Crop") {
      const scaleX = realImgWidth / this.imgWidth
      const scaleY = realImgHeight / this.imgHeight
      const left = this.cropBox.x * scaleX
      const top = this.cropBox.y * scaleY
      const right = realImgWidth - left - this.cropBox.w * scaleX
      const bottom = realImgHeight - top - this.cropBox.h * scaleY
      return { left, top, right, bottom }
    }

    if (this.imageFit === "Fill") {
      const scaleX = realImgWidth / this.imgWidth
      const scaleY = realImgHeight / this.imgHeight
      const visibleWidth = this.shownAreaWidth * scaleX
      const visibleHeight = this.shownAreaHeight * scaleY
      const rawLeft = ((this.imgWidth - this.shownAreaWidth) / 2 - this.fillDragX) * scaleX
      const rawTop = ((this.imgHeight - this.shownAreaHeight) / 2 - this.fillDragY) * scaleY
      const left = Math.max(0, Math.min(realImgWidth - visibleWidth, rawLeft))
      const top = Math.max(0, Math.min(realImgHeight - visibleHeight, rawTop))
      return {
        left,
        top,
        right: Math.max(0, realImgWidth - left - visibleWidth),
        bottom: Math.max(0, realImgHeight - top - visibleHeight),
      }
    }

    return { left: 0, top: 0, right: 0, bottom: 0 }
  }

  uploadCroppedImageToBlob() {
    this.uploading = true

    const img = new Image()
    img.src = this.imageSrc
    img.crossOrigin = "anonymous"

    img.onload = () => {
      const { left: cropLeftPx, top: cropTopPx, right: cropRightPx, bottom: cropBottomPx } = this.computeCropPx(img.width, img.height)

      const cropWidth = img.width - cropLeftPx - cropRightPx
      const cropHeight = img.height - cropTopPx - cropBottomPx

      const canvas = document.createElement("canvas")
      canvas.width = this.imageFit === "Crop" ? cropWidth : this.outputWidth
      canvas.height = this.imageFit === "Crop" ? cropHeight : this.outputHeight

      const ctx = canvas.getContext("2d")
      if (!ctx) return console.error("Canvas context could not be created")

      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = "high"
      ctx.drawImage(img, cropLeftPx, cropTopPx, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height)

      const hasTransparency = (() => {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data
        for (let i = 3; i < imageData.length; i += 4) {
          if (imageData[i] < 255) return true
        }
        return false
      })()

      const imageType = hasTransparency ? "image/png" : "image/jpeg"
      const fileExtension = hasTransparency ? "png" : "jpg"

      canvas.toBlob(
        (blob) => {
          if (!blob) return console.error("Failed to create blob from canvas")

          const dataTransfer = new DataTransfer()
          const fileList = dataTransfer.files

          const hotelId = this.hotelId
          let ImageId = ""
          let ImageUrl = ""
          if (this.image.imageCropping?.oldImageLink) {
            ImageId = this.image.imageCropping.imageId
            ImageUrl = Environment.BlobStorageUrl + "/medialibrary/" + hotelId + "/" + ImageId + "." + fileExtension + "?date=" + new Date().toString()
          } else {
            ImageId = uuidv4()
            ImageUrl = Environment.BlobStorageUrl + "/medialibrary/" + hotelId + "/" + ImageId + "." + fileExtension
          }
          const file = new File([blob], `cropped-${ImageId}.${fileExtension}`, { type: imageType })
          dataTransfer.items.add(file)

          this.utilityApiService.MediaLibrary.getAllMedia(this.hotelId, true).subscribe((mediaList) => {
            if (mediaList) {
              if (this.isEmailBuilder) {
                const folderId = this.mediaLibraryCleanupService.findEntityFolder(mediaList, this.emailBuilderId)?.id ?? ""
                if (folderId === "") {
                  this.utilityApiService.MediaLibrary.createFolder(hotelId, hotelId, encodeURIComponent(this.emailBuilderName) + ":" + this.emailBuilderId).subscribe((media) => {
                    if (media) {
                      this.utilityApiService.MediaLibrary.uploadFile(hotelId, media.id, fileList, null, ImageId).subscribe({
                        next: () => this.finalizeSave(ImageId, ImageUrl, cropLeftPx, cropTopPx, cropBottomPx, cropRightPx),
                        error: () => this.toasterService.error({ title: "Couldn't upload file", message: "Try again or contact AeroGuest Support" }),
                      })
                    }
                  })
                } else {
                  this.utilityApiService.MediaLibrary.uploadFile(hotelId, folderId, fileList, null, ImageId).subscribe({
                    next: () => this.finalizeSave(ImageId, ImageUrl, cropLeftPx, cropTopPx, cropBottomPx, cropRightPx),
                    error: (err) => console.error("Upload failed", err),
                  })
                }
              } else {
                const hd = this.hdService.getHotelDirectory()
                const folderId = this.mediaLibraryCleanupService.findEntityFolder(mediaList, hd.id)?.id ?? ""
                if (folderId === "") {
                  this.utilityApiService.MediaLibrary.createFolder(hotelId, hotelId, encodeURIComponent(hd.name) + ":" + hd.id).subscribe((media) => {
                    if (media) {
                      this.utilityApiService.MediaLibrary.uploadFile(hotelId, media.id, fileList, null, ImageId).subscribe({
                        next: () => this.finalizeSave(ImageId, ImageUrl, cropLeftPx, cropTopPx, cropBottomPx, cropRightPx),
                        error: (err) => console.error("Upload failed", err),
                      })
                    }
                  })
                } else {
                  this.utilityApiService.MediaLibrary.uploadFile(hotelId, folderId, fileList, null, ImageId).subscribe({
                    next: () => this.finalizeSave(ImageId, ImageUrl, cropLeftPx, cropTopPx, cropBottomPx, cropRightPx),
                    error: (err) => console.error("Upload failed", err),
                  })
                }
              }
            }
          })
        },
        imageType,
        1.0,
      )
    }

    img.onerror = (e) => console.error("Failed to load original image", e)
  }

  private finalizeSave(imageId: string, imageUrl: string, cropLeft: number, cropTop: number, cropBottom: number, cropRight: number) {
    if (!this.image.imageCropping?.oldImageLink) {
      this.image.imageCropping = {
        oldImageLink: this.image.imageLink,
        imageId,
        cropLeft,
        cropTop,
        cropBottom,
        cropRight,
        imageFit: this.imageFit,
        originalWidth: this.outputWidth,
        originalHeight: this.outputHeight,
      }
    } else {
      this.image.imageCropping.cropLeft = cropLeft
      this.image.imageCropping.cropTop = cropTop
      this.image.imageCropping.cropBottom = cropBottom
      this.image.imageCropping.cropRight = cropRight
      this.image.imageCropping.imageFit = this.imageFit
    }
    this.image.imageLink = imageUrl
    this.uploading = false
    this.dialogRef.close(this.image)
  }
}
